/**
 * The interactive table view: the map's nodes in reading order (or sorted by
 * any column), one row per node. A click, or Enter / Space on a focused row,
 * selects the node; ArrowUp / ArrowDown move between rows. The cells are the
 * static table's own (FlowTableRowCells), so the two tables read the same.
 */
import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactElement } from "react";
import { FlowTableRowCells } from "./FlowMapTableStatic.js";
import { IconChevronDown, IconChevronUp, IconChevronsUpDown } from "./icons.js";
import type { FlowMapDoc, FlowMapLabels, FlowNodeMark } from "./model.js";
import { FLOW_TABLE_COLUMNS, flowTableRows, sortFlowRows, type FlowSortDir, type FlowSortKey } from "./table.js";

const NUMERIC: ReadonlySet<FlowSortKey> = new Set<FlowSortKey>(["order", "steps", "links"]);

export interface FlowMapTableProps {
  doc: FlowMapDoc;
  /** Language of the map's text. */
  locale?: string;
  labels: FlowMapLabels;
  selectedId: string | null;
  onSelectNode: (id: string) => void;
  /** Shown in the one row of an empty map. */
  emptyText: string;
  /** Accessible table caption (visually hidden); default: the table view's name. */
  caption?: string;
  /** The host's marks by node id (FlowMapProps.nodeMarks). */
  marks?: Readonly<Record<string, FlowNodeMark>>;
}

export function FlowMapTable({ doc, locale, labels, selectedId, onSelectNode, emptyText, caption, marks }: FlowMapTableProps): ReactElement {
  const [sort, setSort] = useState<{ key: FlowSortKey; dir: FlowSortDir }>({ key: "order", dir: "asc" });
  const rows = useMemo(() => flowTableRows(doc, locale), [doc, locale]);
  const sorted = useMemo(
    () => (sort.key === "order" && sort.dir === "asc" ? rows : sortFlowRows(rows, sort.key, sort.dir, locale)),
    [rows, sort, locale],
  );
  const body = useRef<HTMLTableSectionElement>(null);

  // A node selected elsewhere (the canvas, a link in the panel) is brought into view.
  useEffect(() => {
    if (!selectedId) return;
    const row = Array.from(body.current?.rows ?? []).find((r) => r.dataset.node === selectedId);
    row?.scrollIntoView?.({ block: "nearest" });
  }, [selectedId]);

  const toggle = (key: FlowSortKey): void =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  const onRowKeyDown = (e: KeyboardEvent<HTMLTableRowElement>, id: string): void => {
    if (e.target !== e.currentTarget) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onSelectNode(id);
    } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const next = e.key === "ArrowDown" ? e.currentTarget.nextElementSibling : e.currentTarget.previousElementSibling;
      (next as HTMLElement | null)?.focus();
    }
  };

  return (
    <div className="wa-fm-table-wrap">
      <table className="wa-fm-table">
        <caption className="wa-fm__sr">{caption?.trim() ? caption : labels.tableView}</caption>
        <thead>
          <tr>
            {FLOW_TABLE_COLUMNS.map((c) => {
              const active = sort.key === c.key;
              const name = labels[c.label];
              const action = labels.sortBy.replace("{column}", () => name);
              return (
                <th
                  key={c.key}
                  scope="col"
                  className={NUMERIC.has(c.key) ? "wa-fm-table__num" : undefined}
                  aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : undefined}
                >
                  <button type="button" className="wa-fm-table__sort" aria-label={action} title={action} onClick={() => toggle(c.key)}>
                    <span>{name}</span>
                    {!active ? (
                      <IconChevronsUpDown size={12} />
                    ) : sort.dir === "asc" ? (
                      <IconChevronUp size={12} />
                    ) : (
                      <IconChevronDown size={12} />
                    )}
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody ref={body}>
          {sorted.length === 0 ? (
            <tr>
              <td className="wa-fm-table__empty" colSpan={FLOW_TABLE_COLUMNS.length}>
                {emptyText}
              </td>
            </tr>
          ) : (
            sorted.map((row) => {
              const selected = row.id === selectedId;
              const mark = marks?.[row.id];
              const rowClass = [mark ? `is-mark-${mark}` : "", selected ? "is-selected" : ""].filter(Boolean).join(" ");
              return (
                <tr
                  key={row.id}
                  data-node={row.id}
                  data-clickable=""
                  tabIndex={0}
                  className={rowClass || undefined}
                  aria-current={selected ? "true" : undefined}
                  onClick={() => onSelectNode(row.id)}
                  onKeyDown={(e) => onRowKeyDown(e, row.id)}
                >
                  <FlowTableRowCells row={row} labels={labels} />
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
